import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Menu,
  MenuItem,
  TextField,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stage, Layer, Image as KonvaImage, Rect, Circle, Group, Text, Path } from 'react-konva';
import { default as api } from '../../../services/api';
import axios from 'axios';

// Annotation modes
const MODE = {
  VIEW: 'view',
  ANNOTATE: 'annotate',
};

// Focus modes for annotation visibility
const FOCUS_MODE = {
  OFF: 'off',
  SELECTED: 'selected',
};

// Status colors
const STATUS_COLORS = {
  'draft': '#FFC107',
  'validated': '#4CAF50',
  'rejected': '#F44336',
  'approved': '#4CAF50',
};

// Box style configuration
const BOX_STYLE = {
  STATUS: {
    DRAFT: { stroke: '#FFC107', strokeWidth: 2, dash: [5, 5] },
    VALIDATED: { stroke: '#4CAF50', strokeWidth: 2, dash: null },
    APPROVED: { stroke: '#4CAF50', strokeWidth: 2, dash: null },
    REJECTED: { stroke: '#F44336', strokeWidth: 2, dash: [2, 2] }
  },
  ANNOTATE: {
    NORMAL: { stroke: 'yellow', strokeWidth: 2, dash: [5, 5] },
    SELECTED: { stroke: 'orange', strokeWidth: 3, dash: [5, 5] },
    DRAWING: { stroke: 'yellow', strokeWidth: 2, dash: [5, 5] }
  },
  VIEW: {
    NORMAL: { stroke: 'blue', strokeWidth: 2, dash: null }
  }
};

// Anchor points for resizing
const ANCHORS = [
  { x: 0, y: 0, cursor: 'nw-resize' },
  { x: 0.5, y: 0, cursor: 'n-resize' },
  { x: 1, y: 0, cursor: 'ne-resize' },
  { x: 1, y: 0.5, cursor: 'e-resize' },
  { x: 1, y: 1, cursor: 'se-resize' },
  { x: 0.5, y: 1, cursor: 's-resize' },
  { x: 0, y: 1, cursor: 'sw-resize' },
  { x: 0, y: 0.5, cursor: 'w-resize' },
];

// Label dimension constants for smart positioning
const LABEL_DIMENSIONS = {
  STATUS: { width: 115, height: 18, offsetY: 20, textOffsetX: 10, textOffsetY: 6 },
  DEFECT_TYPE: { height: 18, minWidth: 120, offsetY: 2, textOffsetX: 5, textOffsetY: 6 }
};

// Helper to calculate smart label positions based on image bounds
const calculateLabelPositions = (annotation, imageBounds) => {
  const { y, height } = annotation;
  const { top: imageTop, bottom: imageBottom } = imageBounds;

  const statusH = LABEL_DIMENSIONS.STATUS.height;
  const defectH = LABEL_DIMENSIONS.DEFECT_TYPE.height;
  const gap = 2;

  // Default positions
  const statusAboveY = y - LABEL_DIMENSIONS.STATUS.offsetY;
  const defectBelowY = y + height + LABEL_DIMENSIONS.DEFECT_TYPE.offsetY;

  // Check overflows
  const statusOverflowsTop = statusAboveY < imageTop;
  const defectOverflowsBottom = (defectBelowY + defectH) > imageBottom;

  // Case 1: Both fit in default positions
  if (!statusOverflowsTop && !defectOverflowsBottom) {
    return {
      statusY: statusAboveY,
      defectY: defectBelowY
    };
  }

  // Case 2: Status overflows top → stack both below
  if (statusOverflowsTop && !defectOverflowsBottom) {
    return {
      statusY: y + height + gap,                // Status directly below box
      defectY: y + height + gap + statusH + gap // Defect stacked below status
    };
  }

  // Case 3: Defect overflows bottom → stack both above
  if (!statusOverflowsTop && defectOverflowsBottom) {
    return {
      defectY: y - defectH - gap,                    // Defect directly above box
      statusY: y - defectH - gap - statusH - gap     // Status stacked above defect
    };
  }

  // Case 4: Both overflow → stack both below (prioritize visibility)
  return {
    statusY: y + height + gap,
    defectY: y + height + gap + statusH + gap
  };
};

// Helper to convert an array of points to an SVG path string for Konva
const pointsToPath = (points) => {
  if (!points || points.length === 0) return '';
  const path = points.map((p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    return `L ${p.x} ${p.y}`;
  }).join(' ');
  return path + ' Z'; // Z closes the path
};

const Enhanced_AerialImageViewerModal = ({
  open,
  onClose,
  photo,
  onDataMutated,
  viewOnlyMode = false,
  photos = [],
  currentIndex = 0,
  onNavigate = null
}) => {
  const theme = useTheme();
  
  // Image state
  const [image, setImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  
  // Annotation state
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [mode, setMode] = useState(MODE.VIEW);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  const [activeAnchorIndex, setActiveAnchorIndex] = useState(null);
  
  // Canvas state
  const stageRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const isAnchorDraggingRef = useRef(false);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  
  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = useState(false);
  const isZoomedRef = useRef(false);
  
  // Focus mode state
  const [focusMode, setFocusMode] = useState(FOCUS_MODE.OFF);
  const focusModeRef = useRef(FOCUS_MODE.OFF);
  const annotationsRef = useRef([]);
  const selectedAnnotationRef = useRef(null);
  const photosRef = useRef([]);
  const currentIndexRef = useRef(0);
  const onNavigateRef = useRef(null);
  
  // Data state
  const [defectTypes, setDefectTypes] = useState([]);
  const [showSegmentation, setShowSegmentation] = useState(true);
  
  // Segmentation color state
  const [useRandomColors, setUseRandomColors] = useState(false);
  const [segmentationColors, setSegmentationColors] = useState(new Map());
  
  // UI state
  const [apiStatus, setApiStatus] = useState({ open: false, severity: 'info', message: '' });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [defectTypeMenuAnchor, setDefectTypeMenuAnchor] = useState(null);
  const [selectedStatusAnnotation, setSelectedStatusAnnotation] = useState(null);
  const [selectedDefectTypeAnnotation, setSelectedDefectTypeAnnotation] = useState(null);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const [notesEditDialogOpen, setNotesEditDialogOpen] = useState(false);
  const [editingNotesAnnotation, setEditingNotesAnnotation] = useState(null);
  const [editingNotes, setEditingNotes] = useState('');
  
  const statusOptions = ['draft', 'approved', 'rejected'];

  // Function to generate a random bright color
  const generateRandomColor = () => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', 
      '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9',
      '#FF8A65', '#81C784', '#64B5F6', '#FFB74D', '#A1887F', '#90A4AE', '#E57373',
      '#9575CD', '#4FC3F7', '#81C784', '#FFF176', '#FF8A65', '#A1887F', '#90A4AE'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Function to randomize segmentation colors per detection
  const randomizeSegmentationColors = () => {
    const newColorMap = new Map();
    
    // Get unique detection IDs from current annotations
    const uniqueDetectionIds = [...new Set(annotations.map(ann => ann.id))];
    
    // Assign a unique color to each detection
    uniqueDetectionIds.forEach(detectionId => {
      newColorMap.set(detectionId, generateRandomColor());
    });
    
    setSegmentationColors(newColorMap);
    setUseRandomColors(true);
    
    console.log(`[AerialImageViewer] Assigned random colors to ${uniqueDetectionIds.length} detections:`, 
      Object.fromEntries(newColorMap));
  };

  // Function to reset to default colors
  const resetSegmentationColors = () => {
    setUseRandomColors(false);
    setSegmentationColors(new Map());
  };

  // Auto-assign colors when annotations change (if random colors are enabled)
  useEffect(() => {
    if (useRandomColors && annotations.length > 0) {
      const newColorMap = new Map(segmentationColors);
      let hasNewDetections = false;
      
      // Check for any new detections that don't have colors assigned yet
      annotations.forEach(annotation => {
        if (!newColorMap.has(annotation.id)) {
          newColorMap.set(annotation.id, generateRandomColor());
          hasNewDetections = true;
        }
      });
      
      if (hasNewDetections) {
        setSegmentationColors(newColorMap);
        console.log(`[AerialImageViewer] Auto-assigned colors to new detections`);
      }
    }
  }, [annotations, useRandomColors, segmentationColors]);

  // Keep refs in sync
  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);
    
  useEffect(() => {
    selectedAnnotationRef.current = selectedAnnotation;
  }, [selectedAnnotation]);

  useEffect(() => {
    focusModeRef.current = focusMode;
  }, [focusMode]);

  useEffect(() => {
    isZoomedRef.current = isZoomed;
  }, [isZoomed]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setImage(null);
      setAnnotations([]);
      setSelectedAnnotation(null);
      setCurrentAnnotation(null);
      setImageError(null);
      setImageLoading(false);
      setFocusMode(FOCUS_MODE.OFF);
      setMode(MODE.VIEW);
      return;
    }
    
    setFocusMode(FOCUS_MODE.OFF);
    setSelectedAnnotation(null);
    
    // Reset random colors when photo changes
    resetSegmentationColors();
    
    if (open && photo) {
      loadImage();
    }
  }, [open, photo]);

  // Load image
  const loadImage = async () => {
    if (!photo || !photo.url) {
      console.error('[AerialImageViewer] No photo or photo URL provided');
      setImageError('No photo provided');
      setImageLoading(false);
      return;
    }

    console.log('[AerialImageViewer] Loading image:', photo.url);
    setImageLoading(true);
    setImageError(null);
    setAnnotations([]);
    setImage(null);

    try {
      const presignedUrlResponse = await api.post(`/uploads/get-secure-url`, { path: photo.url });
      const presignedUrl = presignedUrlResponse.data.url;

      if (!presignedUrl) {
        throw new Error('Presigned URL was not returned from the server.');
      }

      const imageResponse = await axios.get(presignedUrl, { responseType: 'blob' });
      const objectURL = URL.createObjectURL(imageResponse.data);

      const img = new window.Image();
      img.onload = () => {
        console.log('[AerialImageViewer] Image loaded successfully:', img.width, img.height);
        setImage(img);
        setImageLoading(false);
        URL.revokeObjectURL(objectURL);
      };

      img.onerror = (e) => {
        console.error('[AerialImageViewer] Error loading image from blob URL:', e);
        setImageError('Failed to display fetched image. Please try again.');
        setImageLoading(false);
        setImage(null);
        URL.revokeObjectURL(objectURL);
      };

      img.src = objectURL;
    } catch (error) {
      console.error('[AerialImageViewer] Error fetching image via presigned URL:', error);
      let errorMessage = 'Failed to load image.';
      if (error.response) {
        errorMessage += ` Status: ${error.response.status}`;
      } else if (error.request) {
        errorMessage += ' The request was made but no response was received.';
      } else {
        errorMessage += ` ${error.message}`;
      }
      setImageError(errorMessage);
      setImageLoading(false);
      setImage(null);
    }
  };

  // Load defect types
  useEffect(() => {
    if (!open) return;
    
    const fetchDefectTypes = async () => {
      try {
        const response = await api.get('/defect-types');
        if (Array.isArray(response.data)) {
          setDefectTypes(response.data);
          console.log('[AerialImageViewer] Loaded defect types:', response.data.length);
        }
      } catch (error) {
        console.error('[AerialImageViewer] Error loading defect types:', error);
      }
    };
    
    fetchDefectTypes();
  }, [open, photo]);

  // Load existing annotations
  useEffect(() => {
    if (open && photo && photo.id && image && stageSize.width > 0 && stageSize.height > 0) {
      loadExistingAnnotations(photo.id, image);
    }
  }, [open, photo, image, stageSize]);

  const loadExistingAnnotations = async (photoId, loadedImageElement) => {
    console.log('[AerialImageViewer] Loading annotations for photo ID:', photoId);
    if (!photoId) {
      console.error('[AerialImageViewer] Cannot load annotations: photoId is undefined.');
      return;
    }
    
    try {
      setApiStatus({ open: true, severity: 'info', message: 'Loading annotations...' });
      
      const response = await api.get('/aerial-inspections/bounding-boxes', {
        params: { photo_id: photoId }
      });
      
      console.log('[AerialImageViewer] Received annotations:', response.data);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        if (!loadedImageElement) {
          console.warn('[AerialImageViewer] Image element not provided');
          return;
        }
        
        const displayWidth = Math.min(loadedImageElement.width || 0, stageSize.width || 0);
        const displayHeight = Math.min(loadedImageElement.height || 0, stageSize.height || 0);
        const originalWidth = photo.width || loadedImageElement.naturalWidth || displayWidth;
        const originalHeight = photo.height || loadedImageElement.naturalHeight || displayHeight;
        
        const scaleFactorX = displayWidth / originalWidth;
        const scaleFactorY = displayHeight / originalHeight;
        
        const imagePositionX = (stageSize.width - displayWidth) / 2;
        const imagePositionY = (stageSize.height - displayHeight) / 2;
        
        console.log('[AerialImageViewer] Scaling factors:', {
          displayWidth, displayHeight, originalWidth, originalHeight,
          scaleFactorX, scaleFactorY
        });
        
        const loadedAnnotations = response.data.map(box => {
          const originalBoxWidth = box.x2 - box.x1;
          const originalBoxHeight = box.y2 - box.y1;
          
          const displayX = (box.x1 * scaleFactorX) + imagePositionX;
          const displayY = (box.y1 * scaleFactorY) + imagePositionY;
          const displayAnnotationWidth = originalBoxWidth * scaleFactorX;
          const displayAnnotationHeight = originalBoxHeight * scaleFactorY;

          // Process segmentation polygon(s) - handle both single and multiple polygons
          let scaledPolygonPoints = null;
          if (box.segmentation_polygon && Array.isArray(box.segmentation_polygon) && box.segmentation_polygon.length > 0) {
            // Check if this is multiple polygons (array of arrays) or single polygon (array of points)
            const isMultiplePolygons = Array.isArray(box.segmentation_polygon[0]) && Array.isArray(box.segmentation_polygon[0][0]);
            
            console.log(`[AerialImageViewer] Processing segmentation for box ${box.id}:`, {
              isMultiplePolygons,
              polygonCount: isMultiplePolygons ? box.segmentation_polygon.length : 1,
              firstPolygonPointCount: isMultiplePolygons ? box.segmentation_polygon[0]?.length : box.segmentation_polygon.length,
              rawData: box.segmentation_polygon
            });
            
            if (isMultiplePolygons) {
              // Multiple polygons: array of polygon arrays
              scaledPolygonPoints = box.segmentation_polygon.map(polygon => 
                polygon.map(point => ({
                  x: (point[0] * scaleFactorX) + imagePositionX,
                  y: (point[1] * scaleFactorY) + imagePositionY,
                }))
              );
              console.log(`[AerialImageViewer] Processed ${scaledPolygonPoints.length} polygons for box ${box.id}`);
            } else {
              // Single polygon: array of points (backward compatibility)
              scaledPolygonPoints = [box.segmentation_polygon.map(point => ({
                x: (point[0] * scaleFactorX) + imagePositionX,
                y: (point[1] * scaleFactorY) + imagePositionY,
              }))];
              console.log(`[AerialImageViewer] Processed single polygon (backward compatibility) for box ${box.id}`);
            }
          }
          
          return {
            id: box.id.toString(),
            x: displayX,
            y: displayY,
            width: displayAnnotationWidth,
            height: displayAnnotationHeight,
            segmentationPolygon: scaledPolygonPoints,
            defectTypeId: box.defect_type_id,
            defectType: box.defect_type_name,
            status: box.status,
            isAiGenerated: box.is_ai_generated,
            confidenceScore: box.confidence_score,
            notes: box.notes,
            originalX1: box.x1,
            originalY1: box.y1,
            originalX2: box.x2,
            originalY2: box.y2,
            originalDefectTypeId: box.defect_type_id,
            updated_by: box.updated_by,
            created_by_full_name: box.created_by_full_name || null,
            updated_by_full_name: box.updated_by_full_name || null,
            updated_at: box.updated_at || null
          };
        });
        
        setAnnotations(loadedAnnotations);
        setApiStatus({
          open: true,
          severity: 'success',
          message: `Loaded ${loadedAnnotations.length} annotation(s)`
        });
      } else {
        setApiStatus({ open: false });
      }
    } catch (error) {
      console.error('[AerialImageViewer] Error loading annotations:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error loading annotations: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Adjust stage size
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateSize = () => {
      const container = containerRef.current;
      if (container) {
        const newSize = {
          width: container.offsetWidth,
          height: container.offsetHeight,
        };
        console.log('[AerialImageViewer] Updating stage size:', newSize);
        setStageSize(newSize);
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    
    const timeoutId = setTimeout(() => {
      updateSize();
    }, 300);
    
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, [open, image]);

  // Reset mode when switching
  useEffect(() => {
    if (mode === MODE.VIEW) {
      setSelectedAnnotation(null);
      setActiveAnchorIndex(null);
      setCurrentAnnotation(null);
      setIsDrawing(false);
    }
  }, [mode]);

  // Force view mode if viewOnlyMode
  useEffect(() => {
    if (viewOnlyMode) {
      setMode(MODE.VIEW);
    }
  }, [viewOnlyMode, open]);

  // Get box style
  const getBoxStyle = (isSelected, status) => {
    if (status === 'draft') {
      return BOX_STYLE.STATUS.DRAFT;
    } else if (status === 'validated' || status === 'approved') {
      return BOX_STYLE.STATUS.APPROVED;
    } else if (status === 'rejected') {
      return BOX_STYLE.STATUS.REJECTED;
    }
    
    if (mode === MODE.ANNOTATE) {
      return isSelected ? BOX_STYLE.ANNOTATE.SELECTED : BOX_STYLE.ANNOTATE.NORMAL;
    } else {
      return BOX_STYLE.VIEW.NORMAL;
    }
  };

  // Navigation
  const canGoNext = photos.length > 0 && currentIndex < photos.length - 1;
  const canGoPrevious = photos.length > 0 && currentIndex > 0;

  const handlePrevious = () => {
    if (canGoPrevious && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  // Focus mode
  const toggleFocusMode = () => {
    if (focusMode === FOCUS_MODE.OFF) {
      if (selectedAnnotation) {
        setFocusMode(FOCUS_MODE.SELECTED);
      } else {
        setApiStatus({
          open: true,
          severity: 'info',
          message: 'Please select an annotation to enable focus mode.'
        });
      }
    } else {
      setFocusMode(FOCUS_MODE.OFF);
    }
  };

  // Focus on specific annotation (for table actions)
  const focusOnAnnotation = (annotation) => {
    setSelectedAnnotation(annotation);
    setFocusMode(FOCUS_MODE.SELECTED);
    
    // Optional: Auto-zoom to the annotation
    if (annotation) {
      handleZoomToAnnotation(annotation);
    }
  };

  // Cycle through annotations in focus mode
  const cycleFocusToNextAnnotation = () => {
    const list = annotationsRef.current || [];
    if (list.length === 0) return;

    const current = selectedAnnotationRef.current;
    let currentIndex = -1;
    if (current) {
      currentIndex = list.findIndex(ann => ann.id === current.id);
    }

    const nextIndex = (currentIndex + 1) % list.length;
    const nextAnnotation = list[nextIndex];

    setSelectedAnnotation(nextAnnotation);

    // Auto-activate focus mode if not already active
    if (focusModeRef.current === FOCUS_MODE.OFF) {
      setFocusMode(FOCUS_MODE.SELECTED);
    }

    // Do NOT auto-zoom on Tab; Space toggles zoom explicitly
    console.log('[AerialImageViewer] Tab cycling: Set annotation and focus mode', {
      nextAnnotation: nextAnnotation.id,
      focusMode: FOCUS_MODE.SELECTED
    });

    // Show feedback
    setApiStatus({
      open: true,
      severity: 'info',
      message: `Focused on annotation ${nextIndex + 1} of ${list.length}`
    });
  };

  const getVisibleAnnotations = () => {
    if (focusMode === FOCUS_MODE.OFF) {
      return annotations;
    }
    
    if (focusMode === FOCUS_MODE.SELECTED && selectedAnnotation) {
      return annotations.filter(ann => ann.id === selectedAnnotation.id);
    }
    
    return annotations;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;
      
      const target = event.target;
      const tag = target && target.tagName ? target.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if ((photosRef.current || []).length > 0 && currentIndexRef.current > 0 && onNavigateRef.current) {
            setFocusMode(FOCUS_MODE.OFF);
            setSelectedAnnotation(null);
            onNavigateRef.current(currentIndexRef.current - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if ((photosRef.current || []).length > 0 && currentIndexRef.current < photosRef.current.length - 1 && onNavigateRef.current) {
            setFocusMode(FOCUS_MODE.OFF);
            setSelectedAnnotation(null);
            onNavigateRef.current(currentIndexRef.current + 1);
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (focusMode !== FOCUS_MODE.OFF) {
            setFocusMode(FOCUS_MODE.OFF);
          } else {
            handleSafeClose();
          }
          break;
        case 'f':
        case 'F':
          event.preventDefault();
          toggleFocusMode();
          break;
        case 'Tab':
          event.preventDefault();
          // Cycle through annotations using refs to avoid stale closures
          if ((annotationsRef.current || []).length > 0) {
            cycleFocusToNextAnnotation();
          }
          break;
        case ' ': // Space key (Chrome/Edge)
        case 'Space': // Space key (Safari/Firefox modern)
        case 'Spacebar': // Space key (older browsers)
          event.preventDefault(); // Always prevent default for space to avoid page scroll
          console.log('[AerialImageViewer] Space key pressed!', {
            focusMode: focusModeRef.current,
            selectedAnnotation: selectedAnnotationRef.current,
            isZoomed: isZoomedRef.current
          });
          
          // Spacebar toggles zoom in focus mode on selected annotation
          if (focusModeRef.current === FOCUS_MODE.SELECTED && selectedAnnotationRef.current) {
            console.log('[AerialImageViewer] Space: Toggling zoom for selected annotation');
            if (isZoomedRef.current) {
              console.log('[AerialImageViewer] Space: Resetting zoom');
              resetZoom();
            } else {
              console.log('[AerialImageViewer] Space: Zooming to annotation');
              // Get the full annotation object from the annotations array
              const selId = String(selectedAnnotationRef.current.id);
              const fullAnnotation = (annotationsRef.current || []).find(
                (ann) => String(ann.id) === selId
              );
              if (fullAnnotation) {
                handleZoomToAnnotation(fullAnnotation);
              } else {
                console.warn('[AerialImageViewer] Could not find full annotation in array');
              }
            }
          } else {
            console.log('[AerialImageViewer] Space: Not in focus mode or no selected annotation');
          }
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  // Drawing handlers
  const handleStageMouseDown = (e) => {
    if (mode !== MODE.ANNOTATE) return;
    
    const clickedOnStage = e.target === e.target.getStage();
    const clickedOnImage = e.target === imageRef.current;
    
    if (isAnchorDraggingRef.current) return;
    
    if (clickedOnStage || clickedOnImage) {
      setSelectedAnnotation(null);
      setActiveAnchorIndex(null);
    
    const stage = stageRef.current;
      const point = stage.getPointerPosition();
      
      if (!point) return;
      
      const { x, y } = point;
      
      setCurrentAnnotation({
        x,
        y,
        width: 0,
        height: 0,
        id: Date.now().toString(),
      });
      
      setIsDrawing(true);
      console.log('[AerialImageViewer] Starting to draw new annotation at', x, y);
    }
  };

  const handleStageMouseMove = (e) => {
    const stage = stageRef.current;
    if (!stage) return;
    
    if (mode !== MODE.ANNOTATE) {
      stage.container().style.cursor = 'default';
      return;
    }
    
    if (isDragging && selectedAnnotation && !activeAnchorIndex) {
      const point = stage.getPointerPosition();
      if (!point || !dragStartPosition) return;
      
      const dx = point.x - dragStartPosition.x;
      const dy = point.y - dragStartPosition.y;
      
      const newX = selectedAnnotation.startX + dx;
      const newY = selectedAnnotation.startY + dy;
      
      const updatedAnnotations = annotations.map((ann) => {
        if (ann.id === selectedAnnotation.id) {
          return { ...ann, x: newX, y: newY };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      setSelectedAnnotation({ ...selectedAnnotation, x: newX, y: newY });
      stage.container().style.cursor = 'move';
      return;
    }
    
    if (activeAnchorIndex !== null && selectedAnnotation) {
      const point = stage.getPointerPosition();
      if (!point) return;
      
      const rect = { ...selectedAnnotation };
      const anchor = ANCHORS[activeAnchorIndex];
      
      let newProps = {};
      
      switch (activeAnchorIndex) {
        case 0: // top-left
          newProps = {
            x: point.x,
            y: point.y,
            width: rect.x + rect.width - point.x,
            height: rect.y + rect.height - point.y
          };
          break;
        case 4: // bottom-right
          newProps = {
            width: point.x - rect.x,
            height: point.y - rect.y
          };
          break;
        // Add other cases as needed
        default:
          break;
      }
      
      if (newProps.width && newProps.width < 0) {
        newProps.width = 1;
      }
      
      if (newProps.height && newProps.height < 0) {
        newProps.height = 1;
      }
      
      const updatedAnnotations = annotations.map((ann) => {
        if (ann.id === selectedAnnotation.id) {
          return { ...ann, ...newProps };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      setSelectedAnnotation({ ...selectedAnnotation, ...newProps });
      stage.container().style.cursor = anchor.cursor;
      return;
    }
    
    if (isDrawing && currentAnnotation) {
      const point = stage.getPointerPosition();
      if (!point) return;
      
      const newWidth = point.x - currentAnnotation.x;
      const newHeight = point.y - currentAnnotation.y;
      
      setCurrentAnnotation({
        ...currentAnnotation,
        width: newWidth,
        height: newHeight,
      });
      
      stage.container().style.cursor = 'crosshair';
      return;
    }
    
    stage.container().style.cursor = 'default';
  };

  const handleStageMouseUp = () => {
    if (mode !== MODE.ANNOTATE) return;
    
    if (isDragging) {
      setIsDragging(false);
      setDragStartPosition(null);
      return;
    }
    
    if (activeAnchorIndex !== null) {
      setActiveAnchorIndex(null);
      isAnchorDraggingRef.current = false;
      return;
    }
    
    if (!isDrawing || !currentAnnotation) return;
    
    console.log('[AerialImageViewer] Finishing drawing, current annotation:', currentAnnotation);
    
    if (Math.abs(currentAnnotation.width) < 5 || Math.abs(currentAnnotation.height) < 5) {
      setCurrentAnnotation(null);
      setIsDrawing(false);
      console.log('[AerialImageViewer] Discarding too small annotation');
      return;
    }
    
    let annotation = { ...currentAnnotation };
    if (annotation.width < 0) {
      annotation.x += annotation.width;
      annotation.width = Math.abs(annotation.width);
    }
    
    if (annotation.height < 0) {
      annotation.y += annotation.height;
      annotation.height = Math.abs(annotation.height);
    }
    
    annotation.defectType = 'Select type';
    annotation.defectTypeId = null;
    annotation.status = 'draft';
    annotation.isAiGenerated = false;
    
    console.log('[AerialImageViewer] Adding new annotation:', annotation);
    setAnnotations([...annotations, annotation]);
    setSelectedAnnotation(annotation);
    setCurrentAnnotation(null);
    setIsDrawing(false);
    
    setApiStatus({
      open: true,
      severity: 'warning',
      message: 'Please select a defect type before saving'
    });
  };

  const handleAnnotationClick = (annotation) => {
    if (mode !== MODE.ANNOTATE) return;
    setSelectedAnnotation(annotation);
  };

  const handleAnnotationDragStart = (e, annotation) => {
    if (mode !== MODE.ANNOTATE) return;
    e.cancelBubble = true;
    
    if (activeAnchorIndex !== null) return;
    
    const stage = stageRef.current;
    if (!stage) return;
    
    const point = stage.getPointerPosition();
    if (!point) return;
    
    setDragStartPosition(point);
    setSelectedAnnotation({
      ...annotation,
      startX: annotation.x,
      startY: annotation.y
    });
    
    setIsDragging(true);
    stage.container().style.cursor = 'move';
  };

  const handleAnchorMouseDown = (e, anchorIndex) => {
    if (mode !== MODE.ANNOTATE) return;
    e.cancelBubble = true;
    isAnchorDraggingRef.current = true;
    setActiveAnchorIndex(anchorIndex);
  };

  // Delete annotation
  const handleDeleteSelected = async () => {
    if (!selectedAnnotation) return;
    
    try {
      const boundingBoxId = parseInt(selectedAnnotation.id);
      
      if (!isNaN(boundingBoxId)) {
        setApiStatus({ open: true, severity: 'info', message: 'Deleting annotation...' });
        
        await api.delete(`/aerial-inspections/bounding-boxes/${boundingBoxId}`);
        
        setApiStatus({ open: true, severity: 'success', message: 'Annotation deleted successfully' });
        
        if (onDataMutated) {
          onDataMutated();
        }
        
        const filteredAnnotations = annotations.filter((ann) => ann.id !== selectedAnnotation.id);
        setAnnotations(filteredAnnotations);
        setSelectedAnnotation(null);
      } else {
        const filteredAnnotations = annotations.filter((ann) => ann.id !== selectedAnnotation.id);
        setAnnotations(filteredAnnotations);
        setSelectedAnnotation(null);
      }
    } catch (error) {
      console.error('[AerialImageViewer] Error deleting annotation:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    }
  };

  // Save annotations
  const handleSaveAnnotations = async () => {
    if (!photo?.id) {
      setApiStatus({ open: true, severity: 'error', message: 'Cannot save annotations: Missing photo ID' });
      return;
    }

    try {
      const scaleFactorX = image && photo ? (photo.width || image.naturalWidth || 1) / Math.min(image.width || 1, stageSize.width || 1) : 1;
      const scaleFactorY = image && photo ? (photo.height || image.naturalHeight || 1) / Math.min(image.height || 1, stageSize.height || 1) : 1;
      
      const imagePositionX = image ? (stageSize.width - Math.min(image.width || 0, stageSize.width || 0)) / 2 : 0;
      const imagePositionY = image ? (stageSize.height - Math.min(image.height || 0, stageSize.height || 0)) / 2 : 0;
      
      setApiStatus({ open: true, severity: 'info', message: 'Saving annotations...' });
      
      const existingAnnotations = [];
      const newAnnotations = [];
      
      annotations.forEach(annotation => {
        const isExistingAnnotation = !isNaN(parseInt(annotation.id)) && annotation.originalX1 !== undefined;
        if (isExistingAnnotation) {
          existingAnnotations.push(annotation);
        } else {
          newAnnotations.push(annotation);
        }
      });
      
      let newSaveCount = 0;
      let updateCount = 0;
      let skipCount = 0;
      let failCount = 0;
      
      // Update existing annotations
      for (const annotation of existingAnnotations) {
        try {
          const relativeX = annotation.x - imagePositionX;
          const relativeY = annotation.y - imagePositionY;
          
          const newX1 = Math.round(relativeX * scaleFactorX);
          const newY1 = Math.round(relativeY * scaleFactorY);
          const newX2 = Math.round((relativeX + annotation.width) * scaleFactorX);
          const newY2 = Math.round((relativeY + annotation.height) * scaleFactorY);
          
          const hasPositionChanged = 
            Math.abs(newX1 - annotation.originalX1) > 2 ||
            Math.abs(newY1 - annotation.originalY1) > 2 ||
            Math.abs(newX2 - annotation.originalX2) > 2 ||
            Math.abs(newY2 - annotation.originalY2) > 2;
          
          const hasDefectTypeChanged = annotation.defectTypeId !== annotation.originalDefectTypeId;
          
          if (hasPositionChanged || hasDefectTypeChanged) {
            const updateData = {
              x1: newX1,
              y1: newY1,
              x2: newX2,
              y2: newY2
            };
            
            if (hasDefectTypeChanged) {
              updateData.defect_type_id = annotation.defectTypeId;
            }
            
            const boundingBoxId = parseInt(annotation.id);
            await api.put(`/aerial-inspections/bounding-boxes/${boundingBoxId}`, updateData);
            
            updateCount++;
        } else {
            skipCount++;
          }
        } catch (error) {
          console.error('[AerialImageViewer] Error updating annotation:', error);
          failCount++;
        }
      }
      
      // Save new annotations
      for (const annotation of newAnnotations) {
        if (!annotation.defectTypeId) {
          skipCount++;
          continue;
        }
        
        try {
          const relativeX = annotation.x - imagePositionX;
          const relativeY = annotation.y - imagePositionY;
          
          const x1 = Math.round(relativeX * scaleFactorX);
          const y1 = Math.round(relativeY * scaleFactorY);
          const x2 = Math.round((relativeX + annotation.width) * scaleFactorX);
          const y2 = Math.round((relativeY + annotation.height) * scaleFactorY);
          
          const boundingBoxData = {
            photo_id: photo.id,
            defect_type_id: annotation.defectTypeId,
            x1,
            y1,
            x2,
            y2,
            status: 'draft',
            is_ai_generated: false,
            confidence_score: null,
            notes: annotation.notes || ''
          };
          
          const response = await api.post('/aerial-inspections/bounding-boxes', boundingBoxData);
          
          if (response && response.data) {
            annotation.id = response.data.id?.toString();
            annotation.originalX1 = x1;
            annotation.originalY1 = y1;
            annotation.originalX2 = x2;
            annotation.originalY2 = y2;
            annotation.originalDefectTypeId = annotation.defectTypeId;
          }
          
          newSaveCount++;
        } catch (error) {
          console.error('[AerialImageViewer] Error saving new annotation:', error);
          failCount++;
        }
      }
      
      if (failCount === 0) {
        let message = '';
        const parts = [];
        
        if (newSaveCount > 0) parts.push(`created ${newSaveCount} new`);
        if (updateCount > 0) parts.push(`updated ${updateCount} existing`);
        if (skipCount > 0) parts.push(`skipped ${skipCount} unchanged`);
        
        message = `Success: ${parts.join(', ')} annotation(s)`;
        
        setApiStatus({ open: true, severity: 'success', message });
        
        if (photo.id) {
          loadExistingAnnotations(photo.id, image);
        }
      } else {
        setApiStatus({
          open: true,
          severity: 'warning',
          message: `Partial success: ${newSaveCount + updateCount} saved, ${failCount} failed`
        });
      }
    } catch (error) {
      console.error('[AerialImageViewer] Error in save process:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    }
  };

  // Clear all
  const handleClearAll = () => {
    if (!photo?.id) {
      setApiStatus({ open: true, severity: 'warning', message: 'No photo selected or photo ID is missing.' });
      return;
    }
    setClearAllConfirmOpen(true);
  };

  const confirmClearAll = async () => {
    setClearAllConfirmOpen(false);
    setApiStatus({ open: true, severity: 'info', message: 'Deleting all annotations...' });

    let deletedCount = 0;
    let failedCount = 0;

    const savedAnnotations = annotations.filter(ann => !isNaN(parseInt(ann.id)));

    if (savedAnnotations.length === 0) {
      setAnnotations([]);
      setSelectedAnnotation(null);
      setApiStatus({ open: true, severity: 'info', message: 'No saved annotations to delete.' });
      return;
    }

    for (const annotation of savedAnnotations) {
      try {
        const boundingBoxId = parseInt(annotation.id);
        if (!isNaN(boundingBoxId)) {
          await api.delete(`/aerial-inspections/bounding-boxes/${boundingBoxId}`);
          deletedCount++;
        }
      } catch (error) {
        failedCount++;
        console.error(`[AerialImageViewer] Failed to delete annotation ID ${annotation.id}:`, error);
      }
    }

    if (failedCount > 0) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Failed to delete ${failedCount} annotation(s). ${deletedCount} deleted successfully.`
      });
    } else {
      setApiStatus({
        open: true,
        severity: 'success',
        message: `Successfully deleted ${deletedCount} annotation(s).`
      });
    }

    setAnnotations([]);
      setSelectedAnnotation(null);
      
      if (onDataMutated) {
        onDataMutated();
      }
  };

  // Handle menus
  const handleStatusMenuOpen = (e, annotation) => {
    e.stopPropagation();
    setStatusMenuAnchor({ top: e.clientY, left: e.clientX });
    setSelectedStatusAnnotation(annotation);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedStatusAnnotation(null);
  };

  const handleDefectTypeMenuOpen = (e, annotation) => {
    e.stopPropagation();
    if (annotation.status === 'approved' || annotation.status === 'validated') {
      setApiStatus({
        open: true,
        severity: 'info',
        message: 'Defect type cannot be changed for approved annotations.'
      });
      return;
    }
    setDefectTypeMenuAnchor({ top: e.clientY, left: e.clientX });
    setSelectedDefectTypeAnnotation(annotation);
  };

  const handleDefectTypeMenuClose = () => {
    setDefectTypeMenuAnchor(null);
    setSelectedDefectTypeAnnotation(null);
  };

  const handleStatusChange = async (newStatus) => {
    setStatusMenuAnchor(null);
    
    if (!selectedStatusAnnotation) return;

    const boundingBoxId = parseInt(selectedStatusAnnotation.id);
    
    if (isNaN(boundingBoxId)) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot update status: This annotation has not been saved yet.'
      });
      return;
    }

    try {
      setApiStatus({ open: true, severity: 'info', message: `Updating status to ${newStatus}...` });
      
      await api.put(`/aerial-inspections/bounding-boxes/${boundingBoxId}`, {
        status: newStatus
      });
      
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === selectedStatusAnnotation.id) {
          return { ...ann, status: newStatus };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      
      if (selectedAnnotation && selectedAnnotation.id === selectedStatusAnnotation.id) {
        setSelectedAnnotation(prev => ({ ...prev, status: newStatus }));
      }
      
      setSelectedStatusAnnotation(null);
      
      setApiStatus({ open: true, severity: 'success', message: `Status updated to ${newStatus}` });

      if (onDataMutated) {
        onDataMutated();
      }
    } catch (error) {
      console.error('[AerialImageViewer] Error updating status:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating status: ${error.message || 'Unknown error'}`
      });
    }
  };

  const handleDefectTypeChange = async (newDefectTypeId, newDefectTypeName) => {
    setDefectTypeMenuAnchor(null);
    
    if (!selectedDefectTypeAnnotation) return;
    
    try {
      const boundingBoxId = parseInt(selectedDefectTypeAnnotation.id);
      const isExistingAnnotation = !isNaN(boundingBoxId);
      
      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === selectedDefectTypeAnnotation.id) {
          return { 
            ...ann,
            defectTypeId: newDefectTypeId,
            defectType: newDefectTypeName,
            isModified: isExistingAnnotation ? true : undefined
          };
        }
        return ann;
      });
      
      setAnnotations(updatedAnnotations);
      
      if (selectedAnnotation && selectedAnnotation.id === selectedDefectTypeAnnotation.id) {
        setSelectedAnnotation(prev => ({ 
          ...prev,
          defectTypeId: newDefectTypeId,
          defectType: newDefectTypeName,
          isModified: isExistingAnnotation ? true : undefined
        }));
      }
      
      setSelectedDefectTypeAnnotation(null);
      
      setApiStatus({
        open: true,
        severity: 'success',
        message: `Defect type set to ${newDefectTypeName}. Click Save to apply changes.`
      });
    } catch (error) {
      console.error('[AerialImageViewer] Error updating defect type:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating defect type: ${error.message || 'Unknown error'}`
      });
    }
  };

  // Notes editing
  const handleEditNotes = (annotation) => {
    setEditingNotesAnnotation(annotation);
    setEditingNotes(annotation.notes || '');
    setNotesEditDialogOpen(true);
  };

  const handleSaveNotesEdit = async () => {
    if (!editingNotesAnnotation) return;

    const boundingBoxId = parseInt(editingNotesAnnotation.id);

    if (isNaN(boundingBoxId)) {
      setApiStatus({
        open: true,
        severity: 'error',
        message: 'Cannot update notes: This annotation has not been saved yet.'
      });
      return;
    }

    setNotesEditDialogOpen(false);

    try {
      setApiStatus({ open: true, severity: 'info', message: 'Updating notes...' });

      await api.put(`/aerial-inspections/bounding-boxes/${boundingBoxId}`, {
        notes: editingNotes.trim() || null
      });

      const updatedAnnotations = annotations.map(ann => {
        if (ann.id === editingNotesAnnotation.id) {
          return { ...ann, notes: editingNotes.trim() || '' };
        }
        return ann;
      });

      setAnnotations(updatedAnnotations);

      if (selectedAnnotation && selectedAnnotation.id === editingNotesAnnotation.id) {
        setSelectedAnnotation(prev => ({ ...prev, notes: editingNotes.trim() || '' }));
      }

      setApiStatus({ open: true, severity: 'success', message: 'Notes updated successfully' });

      if (onDataMutated) {
        onDataMutated();
      }
    } catch (error) {
      console.error('[AerialImageViewer] Error updating notes:', error);
      setApiStatus({
        open: true,
        severity: 'error',
        message: `Error updating notes: ${error.message || 'Unknown error'}`
      });
    } finally {
      setEditingNotesAnnotation(null);
      setEditingNotes('');
    }
  };

  // Zoom functions
  const handleZoomToAnnotation = (annotation) => {
    console.log('[AerialImageViewer] handleZoomToAnnotation called with:', annotation);
    
    if (!stageRef.current || !image || stageSize.width === 0 || stageSize.height === 0) {
      console.log('[AerialImageViewer] Missing stage/image or stageSize not ready, cannot zoom');
      return;
    }
    
    // If already zoomed to this annotation, reset zoom
    if (isZoomedRef.current && selectedAnnotationRef.current && selectedAnnotationRef.current.id === annotation.id) {
      console.log('[AerialImageViewer] Already zoomed to this annotation, resetting zoom');
      resetZoom();
      return;
    }
    
    // Select the annotation
    setSelectedAnnotation(annotation);
    
    // Calculate the center point of the annotation
    const annotationCenterX = annotation.x + annotation.width / 2;
    const annotationCenterY = annotation.y + annotation.height / 2;
    
    // Calculate the zoom level based on annotation size
    // We want the annotation to take up about 50% of the view
    const scaleX = stageSize.width / (annotation.width * 2);
    const scaleY = stageSize.height / (annotation.height * 2);
    const newZoom = Math.min(scaleX, scaleY, 5); // Limit max zoom to 5x
    
    // Calculate new position to center the annotation
    const newPositionX = (stageSize.width / 2) - (annotationCenterX * newZoom);
    const newPositionY = (stageSize.height / 2) - (annotationCenterY * newZoom);
    
    console.log('[AerialImageViewer] Zoom values:', {
      annotationCenter: { x: annotationCenterX, y: annotationCenterY },
      stageSize,
      newZoom,
      newPosition: { x: newPositionX, y: newPositionY }
    });
    
    // Update state
    setZoomLevel(newZoom);
    setStagePosition({ x: newPositionX, y: newPositionY });
    setIsZoomed(true);
    isZoomedRef.current = true;
  };

  const resetZoom = () => {
    console.log('[AerialImageViewer] Resetting zoom');
    setZoomLevel(1);
    setStagePosition({ x: 0, y: 0 });
    setIsZoomed(false);
    isZoomedRef.current = false;
  };

  const handleSafeClose = () => {
    setImage(null);
    setAnnotations([]);
    setSelectedAnnotation(null);
    setCurrentAnnotation(null);
    setIsDrawing(false);
    setIsDragging(false);
    setActiveAnchorIndex(null);
    setDragStartPosition(null);
    setZoomLevel(1);
    setStagePosition({ x: 0, y: 0 });
    setIsZoomed(false);
    setFocusMode(FOCUS_MODE.OFF);
    onClose();
  };

  const handleSnackbarClose = () => {
    setApiStatus(prev => ({ ...prev, open: false }));
  };

  const toggleAnnotationMode = () => {
    if (viewOnlyMode) return;
    setMode(mode === MODE.VIEW ? MODE.ANNOTATE : MODE.VIEW);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <Dialog open={open} onClose={handleSafeClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'background.paper',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Previous Navigation Button */}
            <Tooltip title={canGoPrevious ? "Previous image (←)" : "No previous image"}>
              <span>
              <IconButton
                onClick={handlePrevious}
                  disabled={!canGoPrevious}
                size="small"
                  sx={{
                    bgcolor: canGoPrevious ? 'primary.main' : 'action.disabled',
                    color: canGoPrevious ? 'white' : 'text.disabled',
                    '&:hover': {
                      bgcolor: canGoPrevious ? 'primary.dark' : 'action.disabled',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabled',
                      color: 'text.disabled'
                    }
                  }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
              </span>
            </Tooltip>

            <Box>
              <Typography variant="h6" component="div">
                Aerial Image Viewer
              </Typography>
              {photo && (
                <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {photo.filename || photo.url?.split('/').pop() || 'Aerial Image'} 
                  {image && ` • ${image.width} × ${image.height} px`}
                </Typography>
              )}
              
              {/* Photo Counter */}
              {photos.length > 1 && (
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  backgroundColor: 'action.hover',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.875rem'
                }}>
                  {currentIndex + 1} of {photos.length}
                </Typography>
              )}
            </Box>

            {/* Next Navigation Button */}
            <Tooltip title={canGoNext ? "Next image (→)" : "No next image"}>
              <span>
              <IconButton
                onClick={handleNext}
                  disabled={!canGoNext}
                size="small"
                  sx={{
                    bgcolor: canGoNext ? 'primary.main' : 'action.disabled',
                    color: canGoNext ? 'white' : 'text.disabled',
                    mr: 2,
                    '&:hover': {
                      bgcolor: canGoNext ? 'primary.dark' : 'action.disabled',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabled',
                      color: 'text.disabled'
                    }
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={mode === MODE.ANNOTATE ? "Exit Annotation Mode" : "Enter Annotation Mode"}>
              <span>
                <IconButton
                  onClick={toggleAnnotationMode}
                  aria-label="annotation mode"
                  color={mode === MODE.ANNOTATE ? "primary" : "default"}
                  size="small"
                  disabled={viewOnlyMode}
                >
                  <EditIcon />
          </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={showSegmentation ? "Hide Annotations" : "Show Annotations"}>
              <IconButton
                onClick={() => setShowSegmentation(!showSegmentation)}
                aria-label="toggle annotations visibility"
                color={showSegmentation ? "primary" : "default"}
                size="small"
              >
                {showSegmentation ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title={focusMode === FOCUS_MODE.OFF ? "Focus Mode" : "Exit Focus Mode"}>
              <span>
                <IconButton
                  onClick={toggleFocusMode}
                  aria-label="focus mode"
                  color={focusMode !== FOCUS_MODE.OFF ? "secondary" : "default"}
                  size="small"
                  disabled={viewOnlyMode || annotations.length === 0}
                >
                  <CenterFocusStrongIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Delete Selected">
              <span>
                <IconButton 
                  onClick={handleDeleteSelected} 
                  disabled={!selectedAnnotation || mode !== MODE.ANNOTATE}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
            </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Clear All">
              <span>
                <IconButton 
                  onClick={handleClearAll} 
                  disabled={annotations.length === 0 || mode !== MODE.ANNOTATE}
                  color="warning"
                  size="small"
                >
                  <DeleteIcon />
            </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Save Annotations">
              <span>
                <IconButton 
                  onClick={handleSaveAnnotations} 
                  disabled={annotations.length === 0}
                  color="primary"
                  size="small"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Randomize Segmentation Colors Button */}
            <Tooltip title={useRandomColors ? "Reset to Default Colors" : "Randomize Segmentation Colors"}>
              <IconButton
                onClick={useRandomColors ? resetSegmentationColors : randomizeSegmentationColors}
                aria-label="randomize segmentation colors"
                color={useRandomColors ? "secondary" : "default"}
                size="small"
                disabled={annotations.length === 0 || !annotations.some(ann => ann.segmentationPolygon)}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    background: useRandomColors 
                      ? 'linear-gradient(45deg, #FF6B6B 0%, #4ECDC4 25%, #45B7D1 50%, #96CEB4 75%, #FFEAA7 100%)'
                      : 'linear-gradient(45deg, #757575 0%, #BDBDBD 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: 'white',
                    textShadow: '1px 1px 1px rgba(0,0,0,0.8)'
                  }}
                >
                  🎨
                </Box>
              </IconButton>
            </Tooltip>
            
            <IconButton edge="end" color="inherit" onClick={handleSafeClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {isZoomed && (
              <Tooltip title="Reset Zoom">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<ZoomOutIcon />}
                  onClick={resetZoom}
                >
                  Reset View
                </Button>
              </Tooltip>
            )}
          </Box>

          <Box 
            ref={containerRef}
            sx={{
              overflow: 'hidden',
              maxHeight: '70vh',
              height: '70vh',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 0,
              position: 'relative',
            }}
          >
            {/* Focus Mode Indicator */}
            {focusMode !== FOCUS_MODE.OFF && (
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                bgcolor: 'secondary.main',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                boxShadow: 2
              }}>
                <CenterFocusStrongIcon sx={{ fontSize: '1rem' }} />
                Focus Mode: {selectedAnnotation ? 
                  `${annotations.findIndex(ann => ann.id === selectedAnnotation.id) + 1} of ${annotations.length}` : 
                  'Selected Only'
                }
                {annotations.length > 1 && (
                  <Typography variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                    Press Tab to cycle • Space to zoom
                  </Typography>
                )}
                <IconButton 
                  size="small" 
                  onClick={() => setFocusMode(FOCUS_MODE.OFF)}
                  sx={{ color: 'white', p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: '0.75rem' }} />
                </IconButton>
              </Box>
            )}
            
            {photo ? (
              imageLoading ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography>Loading image...</Typography>
                </Box>
              ) : imageError ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <Typography color="error" sx={{ mb: 2 }}>{imageError}</Typography>
                  <Button variant="contained" onClick={loadImage}>
                    Retry Loading
                  </Button>
                </Box>
              ) : image ? (
                <Stage
                  ref={stageRef}
                  width={stageSize.width}
                  height={stageSize.height}
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                  scaleX={zoomLevel}
                  scaleY={zoomLevel}
                  x={stagePosition.x}
                  y={stagePosition.y}
                  draggable={false}
                >
                  <Layer>
                    <KonvaImage
                      ref={imageRef}
                      image={image}
                      width={Math.min(image.width, stageSize.width)}
                      height={Math.min(image.height, stageSize.height)}
                      x={(stageSize.width - Math.min(image.width, stageSize.width)) / 2}
                      y={(stageSize.height - Math.min(image.height, stageSize.height)) / 2}
                      listening={mode === MODE.ANNOTATE}
                      perfectDrawEnabled={false}
                    />

                    {/* Render existing annotations */}
                    {(() => {
                      // Calculate image bounds for smart label positioning
                      const displayWidth = image ? Math.min(image.width, stageSize.width) : 0;
                      const displayHeight = image ? Math.min(image.height, stageSize.height) : 0;
                      const imagePositionX = (stageSize.width - displayWidth) / 2;
                      const imagePositionY = (stageSize.height - displayHeight) / 2;
                      const imageBounds = {
                        top: imagePositionY,
                        bottom: imagePositionY + displayHeight,
                        left: imagePositionX,
                        right: imagePositionX + displayWidth
                      };

                      return getVisibleAnnotations().map((annotation) => {
                        const isSelected = selectedAnnotation && selectedAnnotation.id === annotation.id && mode === MODE.ANNOTATE;
                        const boxStyle = getBoxStyle(isSelected, annotation.status);
                        const labelPositions = calculateLabelPositions(annotation, imageBounds);
                      
                      return (
                        <Group key={annotation.id}>
                          {/* Render segmentation polygon(s) if they exist and are visible */}
                          {showSegmentation && annotation.segmentationPolygon && annotation.segmentationPolygon.map((polygon, polygonIndex) => {
                            const pathData = pointsToPath(polygon);
                            
                            // Determine fill color: use random color if enabled, otherwise use default box style color
                            const fillColor = useRandomColors && segmentationColors.has(annotation.id) 
                              ? segmentationColors.get(annotation.id) 
                              : boxStyle.stroke;
                            
                            console.log(`[AerialImageViewer] Rendering polygon ${polygonIndex + 1}/${annotation.segmentationPolygon.length} for annotation ${annotation.id}:`, {
                              pointCount: polygon.length,
                              fillColor: fillColor,
                              useRandomColors: useRandomColors,
                              pathData: pathData.substring(0, 100) + '...' // Truncate for readability
                            });
                            
                            return (
                              <Path
                                key={`${annotation.id}-polygon-${polygonIndex}`}
                                data={pathData}
                                fill={fillColor}
                                opacity={0.3}
                                closed={true}
                                listening={false}
                              />
                            );
                          })}
                          
                          {/* Primary bounding box */}
                          <Rect
                            x={annotation.x}
                            y={annotation.y}
                            width={annotation.width}
                            height={annotation.height}
                            fill="transparent"
                            stroke={boxStyle.stroke}
                            strokeWidth={boxStyle.strokeWidth}
                            dash={boxStyle.dash}
                            onClick={() => handleAnnotationClick(annotation)}
                            onMouseDown={(e) => handleAnnotationDragStart(e, annotation)}
                            listening={mode === MODE.ANNOTATE}
                            onMouseOver={(e) => {
                              if (mode === MODE.ANNOTATE) {
                                const container = e.target.getStage().container();
                                container.style.cursor = 'move';
                              }
                            }}
                            onMouseOut={(e) => {
                              const container = e.target.getStage().container();
                              container.style.cursor = 'default';
                            }}
                          />
                          
                          {/* Confidence score */}
                          {annotation.confidenceScore && (
                            <Text
                              x={annotation.x + 5}
                              y={annotation.y + 5}
                              text={`${Math.round(annotation.confidenceScore * 100)}%`}
                              fontSize={12}
                              fill="white"
                              fontStyle="bold"
                              shadowColor="black"
                              shadowBlur={5}
                              shadowOpacity={0.9}
                              listening={false}
                            />
                          )}
                          
                          {/* AI indicator */}
                          {annotation.isAiGenerated && (
                            <Group>
                              <Circle
                                x={annotation.x + 12}
                                y={annotation.y + 12}
                                radius={10}
                                fill="#3f51b5"
                                opacity={0.9}
                                stroke="#ffffff"
                                strokeWidth={1}
                              />
                              <Text
                                x={annotation.x + 7}
                                y={annotation.y + 6}
                                text="AI"
                                fontSize={10}
                                fill="white"
                                fontStyle="bold"
                                listening={false}
                              />
                            </Group>
                          )}
                          
                          {/* Status label - smart positioned */}
                          <Group>
                            <Rect
                              x={annotation.x + annotation.width - 115}
                              y={labelPositions.statusY}
                              width={115}
                              height={18}
                              fill={STATUS_COLORS[annotation.status] || '#9E9E9E'}
                              cornerRadius={6}
                              opacity={0.9}
                              onClick={(e) => !viewOnlyMode && annotation.defectTypeId ? handleStatusMenuOpen(e.evt, annotation) : null}
                              listening={!viewOnlyMode && !!annotation.defectTypeId}
                              cursor={!viewOnlyMode && annotation.defectTypeId ? "pointer" : "default"}
                            />
                            <Text
                              x={annotation.x + annotation.width - 105}
                              y={labelPositions.statusY + 6}
                              text={annotation.status || 'draft'}
                              fontSize={11}
                              fontFamily="Arial"
                              fill="white"
                              fontStyle="bold"
                              listening={false}
                            />
                          </Group>
                          
                          {/* Defect type label - smart positioned */}
                          <Group>
                            <Rect
                              x={annotation.x}
                              y={labelPositions.defectY}
                              width={Math.max(120, (annotation.defectType?.length || 0) * 8)}
                              height={18}
                              fill={!annotation.defectTypeId ? '#FF5722' : 'rgba(63, 81, 181, 0.8)'}
                              cornerRadius={6}
                              opacity={0.9}
                              onClick={(e) => !viewOnlyMode ? handleDefectTypeMenuOpen(e.evt, annotation) : null}
                              listening={!viewOnlyMode}
                              cursor={!viewOnlyMode ? 'pointer' : 'default'}
                            />
                            <Text
                              x={annotation.x + 5}
                              y={labelPositions.defectY + 6}
                              text={annotation.defectType || 'Select type'}
                              fontSize={11}
                              fontFamily="Arial"
                              fill="white"
                              fontStyle="bold"
                              width={Math.max(110, (annotation.defectType?.length || 0) * 8)}
                              ellipsis={true}
                              listening={false}
                            />
                          </Group>
                          
                          {/* Render anchor points for resizing if selected */}
                          {isSelected && ANCHORS.map((anchor, index) => {
                            const anchorX = annotation.x + annotation.width * anchor.x;
                            const anchorY = annotation.y + annotation.height * anchor.y;
                            
                            return (
                              <Circle
                                key={index}
                                x={anchorX}
                                y={anchorY}
                                radius={6}
                                fill="yellow"
                                stroke="orange"
                                strokeWidth={1}
                                onMouseDown={(e) => handleAnchorMouseDown(e, index)}
                                onMouseOver={(e) => {
                                  if (mode === MODE.ANNOTATE) {
                                    const container = e.target.getStage().container();
                                    container.style.cursor = anchor.cursor;
                                  }
                                }}
                                onMouseOut={(e) => {
                                  const container = e.target.getStage().container();
                                  container.style.cursor = 'default';
                                }}
                              />
                            );
                          })}
                        </Group>
                      );
                    });
                    })()}

                    {/* Render current annotation being drawn */}
                    {currentAnnotation && isDrawing && (
                      <Rect
                        stroke={BOX_STYLE.ANNOTATE.DRAWING.stroke}
                        strokeWidth={BOX_STYLE.ANNOTATE.DRAWING.strokeWidth}
                        dash={BOX_STYLE.ANNOTATE.DRAWING.dash}
                        x={currentAnnotation.x}
                        y={currentAnnotation.y}
                        width={currentAnnotation.width}
                        height={currentAnnotation.height}
                        fill="transparent"
                      />
                    )}
                  </Layer>
                </Stage>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <Typography color="error">Failed to load image.</Typography>
                  <Button variant="contained" onClick={loadImage} sx={{ mt: 2 }}>
                    Retry Loading
                  </Button>
        </Box>
              )
            ) : (
        <Box sx={{
          display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <Typography variant="body1" color="text.secondary">
                  No photo selected.
            </Typography>
              </Box>
            )}
          </Box>

          {/* Annotation Coordinates Table */}
          {annotations.length > 0 && image && (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '250px', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Defect Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getVisibleAnnotations().map((annotation, index) => {
                    const isZoomedToThis = isZoomed && selectedAnnotation && selectedAnnotation.id === annotation.id;
                    
                    return (
                      <TableRow
                key={annotation.id}
                sx={{
                          backgroundColor: selectedAnnotation && selectedAnnotation.id === annotation.id
                            ? 'rgba(255, 215, 0, 0.1)'
                            : 'inherit',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleAnnotationClick(annotation)}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {annotation.originalX1 === undefined ? (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>New</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                              {annotation.id}
                      </Typography>
                          )}
                        </TableCell>
                        <TableCell>{annotation.defectType || 'N/A'}</TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            bgcolor: STATUS_COLORS[annotation.status] || '#9E9E9E',
                            color: 'white',
                            px: 1,
                            py: 0.2,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {annotation.status ? annotation.status.charAt(0).toUpperCase() + annotation.status.slice(1) : 'N/A'}
                    </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 'bold',
                                color: annotation.isAiGenerated ? 'primary.main' : 'text.secondary'
                              }}
                            >
                              {annotation.isAiGenerated ? 'AI' : 'Manual'}
                        </Typography>
                            {annotation.isAiGenerated && <AutoAwesomeIcon sx={{ fontSize: '0.875rem', color: 'primary.main' }} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={annotation.notes || 'No notes'} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                color: annotation.notes ? 'text.primary' : 'text.disabled',
                                fontStyle: annotation.notes ? 'normal' : 'italic'
                              }}
                            >
                              {annotation.notes || 'No notes'}
                        </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {annotation.updated_at ? formatDateTime(annotation.updated_at) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit Notes">
                  <IconButton
                    size="small"
                                color="default"
                    onClick={(e) => {
                      e.stopPropagation();
                                  handleEditNotes(annotation);
                    }}
                                disabled={annotation.originalX1 === undefined}
                  >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                            </Tooltip>
                            <Tooltip title="Focus on This Annotation">
                              <IconButton
                                size="small"
                                color={focusMode === FOCUS_MODE.SELECTED && selectedAnnotation?.id === annotation.id ? "secondary" : "default"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  focusOnAnnotation(annotation);
                                }}
                              >
                                <CenterFocusStrongIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isZoomedToThis ? "Reset Zoom" : "Zoom to Annotation"}>
                              <IconButton
                                size="small"
                                color={isZoomedToThis ? "secondary" : "primary"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleZoomToAnnotation(annotation);
                                }}
                              >
                                {isZoomedToThis ? <ZoomOutIcon sx={{ fontSize: '1rem' }} /> : <ZoomInIcon sx={{ fontSize: '1rem' }} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {mode === MODE.ANNOTATE && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', color: 'white', borderRadius: 1 }}>
              <Typography variant="body2">
                <b>Annotation Mode Active:</b> Click and drag to create a new box. Click a box to select it.
                Drag a box to reposition it, or use the anchor points to resize it.
                Press <b>F</b> to toggle focus mode and hide other annotations. Press <b>Tab</b> to cycle through annotations in focus mode. Press <b>Space</b> to zoom to selected annotation.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleSafeClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirmation Dialog for Clear All */}
      <Dialog 
        open={clearAllConfirmOpen} 
        onClose={() => setClearAllConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            bgcolor: '#ffebee', 
            width: 40, 
            height: 40, 
            borderRadius: '50%' 
          }}>
            <DeleteIcon color="error" />
        </Box>
          <Typography variant="h6">Confirm Deletion</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete all annotations for this aerial image?
          </Typography>
          
          <Box sx={{ 
            bgcolor: '#ffebee', 
            p: 2, 
            borderRadius: 1, 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}>
            <WarningIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error.main">
              This will delete {annotations.filter(ann => !isNaN(parseInt(ann.id))).length} annotation(s) from the database.
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => setClearAllConfirmOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={confirmClearAll}
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            Delete Annotations
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for API feedback */}
      <Snackbar 
        open={apiStatus.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={apiStatus.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {apiStatus.message}
        </Alert>
      </Snackbar>
      
      {/* Status Change Menu */}
      <Menu
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={statusMenuAnchor}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        {statusOptions.map((status) => (
          <MenuItem 
            key={status} 
            onClick={() => handleStatusChange(status)}
            disabled={selectedStatusAnnotation?.status === status}
            sx={{ 
              fontSize: '13px',
              padding: '8px 16px',
              '&::before': {
                content: '""',
                display: 'block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: STATUS_COLORS[status] || '#9E9E9E',
                marginRight: 1
              }
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </MenuItem>
        ))}
      </Menu>

      {/* Defect Type Change Menu */}
      <Menu
        open={Boolean(defectTypeMenuAnchor)}
        onClose={handleDefectTypeMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={defectTypeMenuAnchor}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            overflowY: 'auto',
          }
        }}
      >
        {defectTypes.length > 0 ? (
          defectTypes.map((defectType) => (
            <MenuItem 
              key={defectType.id} 
              onClick={() => handleDefectTypeChange(defectType.id, defectType.name)}
              disabled={selectedDefectTypeAnnotation?.defectTypeId === defectType.id}
              sx={{ 
                fontSize: '13px',
                padding: '8px 16px',
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#3f51b5',
                  marginRight: 1
                }
              }}
            >
              {defectType.name.charAt(0).toUpperCase() + defectType.name.slice(1)}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled sx={{ fontSize: '13px', padding: '8px 16px', color: 'text.secondary' }}>
            No defect types available
          </MenuItem>
        )}
      </Menu>

      {/* Notes Editing Dialog */}
      <Dialog
        open={notesEditDialogOpen}
        onClose={() => setNotesEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 35px rgba(0,0,0,0.18)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          pb: 2,
          bgcolor: 'primary.light',
          color: 'primary.contrastText'
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            <EditIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Annotation Notes
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Update notes for annotation #{editingNotesAnnotation?.id}:
          </Typography>
          
          <TextField
            label="Notes"
            multiline
            rows={5}
            fullWidth
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.target.value)}
            placeholder="Add detailed notes about this annotation..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
        <Button 
            onClick={() => setNotesEditDialogOpen(false)}
            variant="outlined"
          color="inherit"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
              borderColor: 'divider'
          }}
        >
            Cancel
        </Button>
          <Button
            onClick={handleSaveNotesEdit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100
            }}
          >
            Save Notes
          </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Enhanced_AerialImageViewerModal;
